import React, { useEffect, useRef, useState } from "react";
import "./Radio.less";

interface RadioProps {
   children?: React.ReactNode;
   autoFocus?: boolean;
   checked?: boolean;
   defaultChecked?: boolean;
   disabled?: boolean;
   value?: string | number;
   key?: React.Key;
   className?: string;
   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   name?: string;
   optionType?: "default" | "button";
   style?: React.CSSProperties;
   id?: any;
}

export const Radio: React.FC<RadioProps> = ({
   children,
   disabled = false,
   checked,
   defaultChecked,
   autoFocus,
   value,
   key,
   className = "",
   onChange,
   name,
   optionType,
   style,
   id
}) => {
   const radioBtnRef = useRef<HTMLInputElement>(null);
   const [isChecked, setIsChecked] = useState(false);
   const radioOptionType = children ? optionType : "default";

   useEffect(() => {
      setIsChecked(false);
      if (!radioBtnRef.current?.checked) return;
      setIsChecked(radioBtnRef.current?.checked);
   }, [radioBtnRef.current?.checked]);

   const handleInputClick = () => {
      const inputElement = document.getElementById(id);
      if (inputElement && inputElement instanceof HTMLInputElement) {
         inputElement.click();
      }
   };

   const radio = (
      <span
         className={`bp2-radio-${radioOptionType} ${
            disabled ? "bp2-radio--disabled" : ""
         } ${className}`}
      >
         <input
            type="radio"
            id={id}
            ref={radioBtnRef}
            className={`bp2-radio-${radioOptionType}__input`}
            disabled={disabled}
            defaultChecked={defaultChecked}
            checked={checked}
            value={value}
            key={key}
            onChange={onChange}
            autoFocus={autoFocus}
            name={name}
         />
         <span className="bp2-radio-inner" />
      </span>
   );

   return children ? (
      <label
         className={`bp2-radio-${radioOptionType}-wrapper ${
            disabled ? "bp2-radio--disabled" : ""
         } ${isChecked ? "bp2-radio--checked" : ""} ${className}`}
         style={style}
      >
         {radio}
         <span onClick={() => handleInputClick()}>{children}</span>
      </label>
   ) : (
      radio
   );
};

interface RadioOption {
   label: React.ReactNode;
   value: string;
   disabled?: boolean;
}

interface RadioGroupProps {
   children?: React.ReactElement<RadioProps>[];
   buttonStyle?: "outline" | "solid";
   defaultValue?: string | number;
   disabled?: boolean;
   name?: string;
   options?: RadioOption[];
   optionType?: "default" | "button";
   size?: "large" | "small";
   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   className: string | null;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
   children,
   className = "",
   defaultValue,
   buttonStyle = "outline",
   disabled,
   name,
   options,
   optionType = "default",
   size,
   onChange
}) => {
   const modifiedChildren = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
         return React.cloneElement(child, {
            onChange: onChange,
            name: name ? name : "bp2-radio-group-name",
            optionType: optionType,
            defaultChecked: String(defaultValue) === String(child?.props?.value),
            disabled: disabled ? disabled : child?.props?.disabled
         });
      }
      return child;
   });

   return (
      <div
         className={`${className} bp2-radio-group--${buttonStyle} ${
            size && optionType === "button" ? `bp2-radio-group--${size}` : ""
         }`}
      >
         {options && options?.length > 0
            ? options?.map((option) => (
                 <Radio
                    onChange={onChange}
                    optionType={optionType}
                    name={name ? name : "bp2-radio-group-name"}
                    key={option?.value}
                    value={option?.value}
                    disabled={disabled ? disabled : option?.disabled}
                    defaultChecked={String(defaultValue) === String(option?.value)}
                 >
                    {/* {option?.label} */}
                 </Radio>
              ))
            : modifiedChildren}
      </div>
   );
};
